import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Building2, Globe, MapPin, Tag, Search, Radio, Plus, X,
  Loader2, AlertCircle, CheckCircle, Info, HelpCircle
} from 'lucide-react';

const INDUSTRY_OPTIONS = [
  'Manufacturing', 'Food & Beverage', 'Bakery', 'Energy', 'Pharmaceuticals',
  'Construction', 'Agriculture', 'Packaging', 'Chemicals', 'Logistics',
  'Automotive', 'Textiles', 'Paper & Pulp', 'Mining', 'Water & Waste',
];

const GEO_OPTIONS = [
  { code: 'FI', name: 'Finland' }, { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' }, { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' }, { code: 'LV', name: 'Latvia' },
  { code: 'LT', name: 'Lithuania' }, { code: 'PL', name: 'Poland' },
  { code: 'CZ', name: 'Czechia' }, { code: 'SK', name: 'Slovakia' },
  { code: 'HU', name: 'Hungary' }, { code: 'RO', name: 'Romania' },
  { code: 'BG', name: 'Bulgaria' }, { code: 'DE', name: 'Germany' },
  { code: 'AT', name: 'Austria' }, { code: 'CH', name: 'Switzerland' },
  { code: 'NL', name: 'Netherlands' }, { code: 'BE', name: 'Belgium' },
  { code: 'LU', name: 'Luxembourg' }, { code: 'FR', name: 'France' },
  { code: 'GB', name: 'United Kingdom' }, { code: 'IE', name: 'Ireland' },
  { code: 'ES', name: 'Spain' }, { code: 'PT', name: 'Portugal' },
  { code: 'IT', name: 'Italy' }, { code: 'GR', name: 'Greece' },
  { code: 'SI', name: 'Slovenia' }, { code: 'HR', name: 'Croatia' },
];

const EVENT_TYPE_OPTIONS = [
  { key: 'funding', label: 'Funding / Grants', description: 'Investment rounds, government grants, subsidies' },
  { key: 'new_line', label: 'New Production Line', description: 'New equipment or production lines' },
  { key: 'expansion', label: 'Expansion', description: 'Capacity expansion, new markets' },
  { key: 'new_plant', label: 'New Plant / Facility', description: 'New factory or facility construction' },
  { key: 'acquisition', label: 'Acquisition', description: 'Company acquisitions or mergers' },
  { key: 'leadership_change', label: 'Leadership Change', description: 'New CEO, CTO, or key hires' },
];

const ALL_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'fi', label: 'Finnish' },
  { code: 'sv', label: 'Swedish' },
  { code: 'no', label: 'Norwegian' },
  { code: 'da', label: 'Danish' },
  { code: 'et', label: 'Estonian' },
  { code: 'lv', label: 'Latvian' },
  { code: 'lt', label: 'Lithuanian' },
  { code: 'pl', label: 'Polish' },
  { code: 'cs', label: 'Czech' },
  { code: 'sk', label: 'Slovak' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'ro', label: 'Romanian' },
  { code: 'bg', label: 'Bulgarian' },
  { code: 'de', label: 'German' },
  { code: 'nl', label: 'Dutch' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'it', label: 'Italian' },
  { code: 'el', label: 'Greek' },
  { code: 'sl', label: 'Slovenian' },
  { code: 'hr', label: 'Croatian' },
];

// Maps each language code to its primary country display (for keyword field labels)
const LANG_TO_GEO = {
  en: { code: 'EN', name: 'English' },
  fi: { code: 'FI', name: 'Finland' },
  sv: { code: 'SE', name: 'Sweden' },
  no: { code: 'NO', name: 'Norway' },
  da: { code: 'DK', name: 'Denmark' },
  et: { code: 'EE', name: 'Estonia' },
  lv: { code: 'LV', name: 'Latvia' },
  lt: { code: 'LT', name: 'Lithuania' },
  pl: { code: 'PL', name: 'Poland' },
  cs: { code: 'CZ', name: 'Czechia' },
  sk: { code: 'SK', name: 'Slovakia' },
  hu: { code: 'HU', name: 'Hungary' },
  ro: { code: 'RO', name: 'Romania' },
  bg: { code: 'BG', name: 'Bulgaria' },
  de: { code: 'DE', name: 'Germany' },
  nl: { code: 'NL', name: 'Netherlands' },
  fr: { code: 'FR', name: 'France' },
  es: { code: 'ES', name: 'Spain' },
  pt: { code: 'PT', name: 'Portugal' },
  it: { code: 'IT', name: 'Italy' },
  el: { code: 'GR', name: 'Greece' },
  sl: { code: 'SI', name: 'Slovenia' },
  hr: { code: 'HR', name: 'Croatia' },
};

// Maps each geo code to the language codes spoken there
const GEO_TO_LANGUAGES = {
  FI: ['fi'], SE: ['sv'], NO: ['no'], DK: ['da'],
  EE: ['et'], LV: ['lv'], LT: ['lt'], PL: ['pl'],
  CZ: ['cs'], SK: ['sk'], HU: ['hu'], RO: ['ro'],
  BG: ['bg'], DE: ['de'], AT: ['de'], CH: ['de', 'fr'],
  NL: ['nl'], BE: ['nl', 'fr'], LU: ['fr', 'de'],
  FR: ['fr'], GB: ['en'], IE: ['en'],
  ES: ['es'], PT: ['pt'], IT: ['it'],
  GR: ['el'], SI: ['sl'], HR: ['hr'],
};

const SignalProfileSetup = ({ onSubmit, existingProfile = null, isAdmin = false }) => {
  const isEdit = !!existingProfile;
  const [userEmail, setUserEmail] = useState('');

  const [formData, setFormData] = useState({
    org_name: existingProfile?.org_name || '',
    org_industry: existingProfile?.org_industry || '',
    org_description: existingProfile?.org_description || '',
    org_website: existingProfile?.org_website || '',
  });

  const [targetIndustries, setTargetIndustries] = useState(
    existingProfile?.target_config?.target_industries || []
  );
  const [targetGeographies, setTargetGeographies] = useState(
    existingProfile?.target_config?.target_geographies || []
  );
  const [keywords, setKeywords] = useState(
    existingProfile?.target_config?.keywords || { en: [] }
  );
  const [eventTypes, setEventTypes] = useState(
    existingProfile?.target_config?.event_types || ['funding', 'new_line', 'expansion', 'new_plant']
  );
  const [watchFeeds, setWatchFeeds] = useState(
    existingProfile?.target_config?.watch_feeds || []
  );

  // Run scheduling — admin controls how often the collector runs for this profile
  const [runIntervalDays, setRunIntervalDays] = useState(
    existingProfile?.run_interval_days || 1
  );
  const [maxDailyRuns, setMaxDailyRuns] = useState(
    existingProfile?.max_daily_runs || 1
  );

  // Country Intelligence Pack state
  const [countryPackEnabled, setCountryPackEnabled] = useState(
    existingProfile?.target_config?.country_pack?.enabled || false
  );
  const [intelligenceMode, setIntelligenceMode] = useState(
    (existingProfile?.target_config?.country_pack?.intelligence_countries?.length > 0) ? 'specific' : 'all'
  );
  const [intelligenceCountries, setIntelligenceCountries] = useState(
    existingProfile?.target_config?.country_pack?.intelligence_countries || []
  );
  const [pkdCodes, setPkdCodes] = useState(
    existingProfile?.target_config?.country_pack?.pkd_codes || []
  );
  const [cpvCodes, setCpvCodes] = useState(
    existingProfile?.target_config?.country_pack?.cpv_codes || []
  );
  const [newPkdCode, setNewPkdCode] = useState('');
  const [newCpvCode, setNewCpvCode] = useState('');
  const [showPkdRef, setShowPkdRef] = useState(false);
  const [showCpvRef, setShowCpvRef] = useState(false);

  // Keyword Axes — two-axis keyword matching (Jani's system)
  const [axisASector, setAxisASector] = useState(
    existingProfile?.target_config?.keyword_axes?.axis_a_sector || {}
  );
  const [axisBEvent, setAxisBEvent] = useState(
    existingProfile?.target_config?.keyword_axes?.axis_b_event || {}
  );
  const [axisCProduct, setAxisCProduct] = useState(
    existingProfile?.target_config?.keyword_axes?.axis_c_product || {}
  );
  const [newAxisKeyword, setNewAxisKeyword] = useState({});

  const [newIndustry, setNewIndustry] = useState('');
  const [newKeyword, setNewKeyword] = useState({});
  const [newFeed, setNewFeed] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showCustomIndustry, setShowCustomIndustry] = useState(
    existingProfile?.org_industry && !INDUSTRY_OPTIONS.includes(existingProfile?.org_industry) ? true : false
  );

  // ─── Validation ───
  const validate = useCallback(() => {
    const errors = [];
    if (!formData.org_name.trim() || formData.org_name.trim().length < 2) errors.push('Company name is required (2+ chars)');
    if (!formData.org_industry.trim() || formData.org_industry.trim().length < 2) errors.push('Industry is required');
    if (formData.org_website && !/^https?:\/\//.test(formData.org_website.trim())) errors.push('Website must start with http:// or https://');
    if (targetIndustries.length < 1) errors.push('At least 1 target industry required');
    if (targetIndustries.length > 20) errors.push('Max 20 target industries');
    if (targetGeographies.length < 1) errors.push('At least 1 target geography required');
    if (targetGeographies.length > 30) errors.push('Max 30 target geographies');
    if (!keywords.en || keywords.en.length < 1) errors.push('At least 1 English keyword required');
    if (eventTypes.length < 1) errors.push('At least 1 event type required');
    if (watchFeeds.length > 10) errors.push('Max 10 watch feeds');
    for (const feed of watchFeeds) {
      if (!/^https?:\/\//.test(feed)) errors.push(`Invalid feed URL: ${feed}`);
    }
    return errors;
  }, [formData, targetIndustries, targetGeographies, keywords, eventTypes, watchFeeds]);

  // ─── Submit ───
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const errors = validate();
    if (errors.length > 0) {
      setSubmitError(errors.join('. '));
      return;
    }

    // Strip HTML tags from text fields
    const sanitize = (str) => str ? str.replace(/<[^>]*>/g, '').trim() : '';

    const profileData = {
      org_name: sanitize(formData.org_name),
      org_description: sanitize(formData.org_description) || null,
      org_industry: sanitize(formData.org_industry),
      org_website: formData.org_website.trim() || null,
      target_config: {
        target_industries: targetIndustries,
        target_geographies: targetGeographies,
        keywords,
        event_types: eventTypes,
        watch_feeds: watchFeeds,
        ...(countryPackEnabled ? {
          country_pack: {
            enabled: true,
            intelligence_countries: intelligenceMode === 'all' ? [] : intelligenceCountries,
            pkd_codes: pkdCodes,
            cpv_codes: cpvCodes,
            max_per_source_per_run: 50,
          },
          ...(countryPackEnabled && (Object.keys(axisASector).length > 0 || Object.keys(axisBEvent).length > 0 || Object.keys(axisCProduct).length > 0) ? {
            keyword_axes: {
              axis_a_sector: axisASector,
              axis_b_event: axisBEvent,
              axis_c_product: axisCProduct,
            }
          } : {}),
        } : {
          country_pack: { enabled: false }
        }),
      },
      // Run scheduling
      run_interval_days: runIntervalDays,
      max_daily_runs: maxDailyRuns,
      // Include user_email when admin creates for another user
      ...(isAdmin && !isEdit && userEmail.trim() ? { user_email: userEmail.trim() } : {}),
    };

    setSubmitting(true);
    try {
      await onSubmit(profileData);
    } catch (err) {
      setSubmitError(err.message || 'Failed to save profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Tag Helpers ───
  const addTag = (list, setList, value, max = 30) => {
    const v = value.trim();
    if (!v || list.includes(v) || list.length >= max) return;
    setList([...list, v]);
  };

  const removeTag = (list, setList, value) => {
    setList(list.filter(t => t !== value));
  };

  const addKeyword = (lang) => {
    const val = (newKeyword[lang] || '').trim();
    if (!val) return;
    const current = keywords[lang] || [];
    if (current.includes(val)) return;
    setKeywords({ ...keywords, [lang]: [...current, val] });
    setNewKeyword({ ...newKeyword, [lang]: '' });
  };

  const removeKeyword = (lang, val) => {
    const current = keywords[lang] || [];
    const updated = current.filter(k => k !== val);
    if (updated.length === 0) {
      const rest = { ...keywords };
      delete rest[lang];
      setKeywords(rest);
    } else {
      setKeywords({ ...keywords, [lang]: updated });
    }
  };

  const addAxisKeyword = (axis, setAxis, lang) => {
    const key = `${axis}_${lang}`;
    const val = (newAxisKeyword[key] || '').trim();
    if (!val) return;
    const current = (axis === 'a' ? axisASector : axis === 'b' ? axisBEvent : axisCProduct)[lang] || [];
    if (current.includes(val)) return;
    const axisData = axis === 'a' ? axisASector : axis === 'b' ? axisBEvent : axisCProduct;
    const setter = axis === 'a' ? setAxisASector : axis === 'b' ? setAxisBEvent : setAxisCProduct;
    setter({ ...axisData, [lang]: [...current, val] });
    setNewAxisKeyword({ ...newAxisKeyword, [key]: '' });
  };

  const removeAxisKeyword = (axis, lang, val) => {
    const axisData = axis === 'a' ? axisASector : axis === 'b' ? axisBEvent : axisCProduct;
    const setter = axis === 'a' ? setAxisASector : axis === 'b' ? setAxisBEvent : setAxisCProduct;
    const current = axisData[lang] || [];
    const updated = current.filter(k => k !== val);
    if (updated.length === 0) {
      const rest = { ...axisData };
      delete rest[lang];
      setter(rest);
    } else {
      setter({ ...axisData, [lang]: updated });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto"
    >
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-2xl mb-4">
          <Radio className="w-8 h-8 text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Signal Profile' : 'Signal Engine — Ready Call List'}
        </h2>
        <p className="text-gray-600 mt-2">
          {isEdit
            ? 'Update your signal monitoring configuration.'
            : 'Set up your signal profile to start receiving buying signals.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ─── Your Organization ─── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Building2 className="w-5 h-5 mr-2 text-primary-600" />
            Your Organization
          </h3>
          <div className="space-y-4">
            {/* Admin: Owner email field (only for new profiles) */}
            {isAdmin && !isEdit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profile Owner Email <span className="text-gray-400 text-xs font-normal">(leave empty to use your own)</span>
                </label>
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="client@company.com"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">The user who will own this signal profile. Each user can only have one profile.</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.org_name}
                onChange={(e) => setFormData({ ...formData, org_name: e.target.value })}
                placeholder="e.g. Acme Industries Ltd"
                maxLength={200}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Industry <span className="text-red-500">*</span>
              </label>
              <select
                value={showCustomIndustry ? '__custom__' : formData.org_industry}
                onChange={(e) => {
                  if (e.target.value === '__custom__') {
                    setShowCustomIndustry(true);
                    setFormData({ ...formData, org_industry: '' });
                  } else {
                    setShowCustomIndustry(false);
                    setFormData({ ...formData, org_industry: e.target.value });
                  }
                }}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              >
                <option value="">Select industry...</option>
                {INDUSTRY_OPTIONS.map(ind => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
                <option value="__custom__">Other (type your own)...</option>
              </select>
              {showCustomIndustry && (
                <input
                  type="text"
                  value={formData.org_industry}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^a-zA-Z\s&-]/g, '');
                    const words = val.trim().split(/\s+/);
                    if (words.length <= 20) {
                      setFormData({ ...formData, org_industry: val });
                    }
                  }}
                  placeholder="Type your industry (letters only, max 20 words)"
                  maxLength={80}
                  className="w-full mt-2 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  required
                  autoFocus
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.org_description}
                onChange={(e) => setFormData({ ...formData, org_description: e.target.value })}
                placeholder="What does your company sell? (1-2 sentences)"
                maxLength={1000}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">{formData.org_description.length}/1000</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="url"
                  value={formData.org_website}
                  onChange={(e) => setFormData({ ...formData, org_website: e.target.value })}
                  placeholder="https://example.com"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ─── Target Customers ─── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Search className="w-5 h-5 mr-2 text-primary-600" />
            Who Are Your Ideal Customers?
          </h3>

          {/* Target Industries */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Industries <span className="text-red-500">*</span>
              <span className="text-xs text-gray-500 ml-2">({targetIndustries.length}/20)</span>
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {targetIndustries.map(ind => (
                <span key={ind} className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                  {ind}
                  <button type="button" onClick={() => removeTag(targetIndustries, setTargetIndustries, ind)} className="ml-1.5 hover:text-blue-900">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <select
                value={newIndustry}
                onChange={(e) => {
                  if (e.target.value && e.target.value !== '__custom_target__') {
                    addTag(targetIndustries, setTargetIndustries, e.target.value, 20);
                    setNewIndustry('');
                  } else {
                    setNewIndustry(e.target.value);
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">+ Add industry...</option>
                {INDUSTRY_OPTIONS.filter(i => !targetIndustries.includes(i)).map(ind => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
                <option value="__custom_target__">+ Add custom industry...</option>
              </select>
            </div>
            {newIndustry === '__custom_target__' && (
              <div className="flex gap-2 mt-2">
                <input
                  type="text"
                  placeholder="Type custom industry (letters only, max 20 words)"
                  maxLength={80}
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z\s&-]/g, '');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = e.target.value.trim();
                      const words = val.split(/\s+/);
                      if (val && words.length <= 20 && /^[a-zA-Z\s&-]+$/.test(val)) {
                        addTag(targetIndustries, setTargetIndustries, val, 20);
                        setNewIndustry('');
                      }
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={(e) => {
                    const input = e.target.closest('div').querySelector('input');
                    const val = input?.value?.trim();
                    const words = (val || '').split(/\s+/);
                    if (val && words.length <= 20 && /^[a-zA-Z\s&-]+$/.test(val)) {
                      addTag(targetIndustries, setTargetIndustries, val, 20);
                      setNewIndustry('');
                    }
                  }}
                  className="px-3 py-2 bg-primary-100 hover:bg-primary-200 text-primary-700 rounded-lg text-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setNewIndustry('')}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Target Geographies */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Target Geographies <span className="text-red-500">*</span>
              <span className="text-xs text-gray-500 ml-2">({targetGeographies.length}/30)</span>
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {targetGeographies.map(geo => {
                const g = GEO_OPTIONS.find(o => o.code === geo);
                return (
                  <span key={geo} className="inline-flex items-center px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm">
                    {g ? `${g.code} — ${g.name}` : geo}
                    <button type="button" onClick={() => removeTag(targetGeographies, setTargetGeographies, geo)} className="ml-1.5 hover:text-green-900">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {GEO_OPTIONS.filter(g => !targetGeographies.includes(g.code)).map(geo => (
                <button
                  key={geo.code}
                  type="button"
                  onClick={() => addTag(targetGeographies, setTargetGeographies, geo.code, 30)}
                  className="px-2.5 py-1 border border-gray-300 rounded-lg text-xs hover:bg-gray-50 transition-colors"
                >
                  + {geo.code}
                </button>
              ))}
            </div>
          </div>

          {/* Keywords per language */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Tag className="w-4 h-4 inline mr-1" />
              Keywords by Language
            </label>
            <p className="text-xs text-gray-500 mb-3">
              {targetGeographies.length === 0
                ? 'Select target geographies above to unlock additional language fields.'
                : `Showing languages for your selected regions. English is always required.`}
            </p>
            {/* Active languages ordered same as GEO_OPTIONS, English always first */}
            {(() => {
              const seen = new Set(['en']);
              const ordered = ['en'];
              GEO_OPTIONS.forEach(geo => {
                if (targetGeographies.includes(geo.code)) {
                  (GEO_TO_LANGUAGES[geo.code] || []).forEach(lc => {
                    if (!seen.has(lc)) { seen.add(lc); ordered.push(lc); }
                  });
                }
              });
              return ordered.map(code => ALL_LANGUAGES.find(l => l.code === code)).filter(Boolean);
            })().map(lang => (
              <div key={lang.code} className="mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-gray-700 w-36 shrink-0">
                    <span className="font-semibold">{LANG_TO_GEO[lang.code]?.code}</span>
                    {' — '}{LANG_TO_GEO[lang.code]?.name}
                    {lang.code === 'en' && <span className="text-red-500 ml-0.5">*</span>}
                  </span>
                  <div className="flex flex-wrap gap-1.5 flex-1">
                    {(keywords[lang.code] || []).map(kw => (
                      <span key={kw} className="inline-flex items-center px-2.5 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs">
                        {kw}
                        <button type="button" onClick={() => removeKeyword(lang.code, kw)} className="ml-1 hover:text-purple-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 ml-36">
                  <input
                    type="text"
                    value={newKeyword[lang.code] || ''}
                    onChange={(e) => setNewKeyword({ ...newKeyword, [lang.code]: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(lang.code); } }}
                    placeholder={`Add ${lang.label.toLowerCase()} keyword...`}
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => addKeyword(lang.code)}
                    className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Event Types */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Types <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {EVENT_TYPE_OPTIONS.map(evt => (
                <label key={evt.key} className="flex items-start p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={eventTypes.includes(evt.key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEventTypes([...eventTypes, evt.key]);
                      } else {
                        setEventTypes(eventTypes.filter(t => t !== evt.key));
                      }
                    }}
                    className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="ml-2">
                    <span className="text-sm font-medium text-gray-900">{evt.label}</span>
                    <p className="text-xs text-gray-500">{evt.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Watch Feeds */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Watch Feeds (RSS URLs)
              <span className="text-xs text-gray-500 ml-2">Optional, max 10</span>
            </label>
            <div className="space-y-2 mb-2">
              {watchFeeds.map((feed, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="flex-1 text-xs text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg truncate">{feed}</span>
                  <button type="button" onClick={() => setWatchFeeds(watchFeeds.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            {watchFeeds.length < 10 && (
              <div className="flex gap-2">
                <input
                  type="url"
                  value={newFeed}
                  onChange={(e) => setNewFeed(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newFeed.trim() && /^https?:\/\//.test(newFeed.trim())) {
                        setWatchFeeds([...watchFeeds, newFeed.trim()]);
                        setNewFeed('');
                      }
                    }
                  }}
                  placeholder="https://example.com/rss"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (newFeed.trim() && /^https?:\/\//.test(newFeed.trim())) {
                      setWatchFeeds([...watchFeeds, newFeed.trim()]);
                      setNewFeed('');
                    }
                  }}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ─── Country Intelligence Pack ─── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center">
            <Globe className="w-5 h-5 mr-2 text-primary-600" />
            Country Intelligence Pack
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            Enable deep intelligence monitoring: grants, procurement, permits, and trade press from specific countries.
            This runs separately from the main signal search and does not affect your RSS/Google News monitoring.
          </p>

          {/* Enable toggle */}
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors mb-4">
            <input
              type="checkbox"
              checked={countryPackEnabled}
              onChange={(e) => setCountryPackEnabled(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Enable Intelligence Pack</span>
              <p className="text-xs text-gray-500">Activates automated collection from government APIs, EU sources, and trade press</p>
            </div>
          </label>

          {/* Run frequency — admin only */}
          {countryPackEnabled && (
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">Collection Frequency</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Run Interval</label>
                  <select
                    value={runIntervalDays}
                    onChange={(e) => setRunIntervalDays(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value={1}>Every day</option>
                    <option value={2}>Every 2 days</option>
                    <option value={3}>Every 3 days</option>
                    <option value={5}>Every 5 days</option>
                    <option value={7}>Weekly</option>
                    <option value={14}>Every 2 weeks</option>
                    <option value={30}>Monthly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Max Runs Per Day</label>
                  <select
                    value={maxDailyRuns}
                    onChange={(e) => setMaxDailyRuns(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value={1}>1 time per day</option>
                    <option value={2}>2 times per day</option>
                    <option value={3}>3 times per day</option>
                    <option value={4}>4 times per day</option>
                    <option value={5}>5 times per day</option>
                  </select>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                How often the system collects new signals for this profile. Runs are spaced at least 2 hours apart.
                {maxDailyRuns > 1 && <span className="text-primary-600 font-medium"> Manual triggers also count toward the daily limit.</span>}
              </p>
              {existingProfile?.last_collector_run_at && (
                <p className="text-xs text-gray-400 mt-1">
                  Last collected: {new Date(existingProfile.last_collector_run_at).toLocaleString()}
                  {existingProfile?.daily_runs_count > 0 && (
                    <span className="ml-2">
                      · {existingProfile.daily_runs_count}/{existingProfile.max_daily_runs || 1} runs today
                    </span>
                  )}
                </p>
              )}
            </div>
          )}

          {countryPackEnabled && (
            <div className="space-y-4 pl-1">
              {/* Country selection mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Intelligence Countries</label>
                <div className="space-y-2 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="intel_mode"
                      checked={intelligenceMode === 'all'}
                      onChange={() => { setIntelligenceMode('all'); setIntelligenceCountries([]); }}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">All my target geographies ({targetGeographies.length} countries)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="intel_mode"
                      checked={intelligenceMode === 'specific'}
                      onChange={() => setIntelligenceMode('specific')}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Select specific countries</span>
                  </label>
                </div>

                {intelligenceMode === 'specific' && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 bg-gray-50 rounded-lg">
                    {targetGeographies.map(cc => {
                      const geo = GEO_OPTIONS.find(g => g.code === cc);
                      return (
                        <label key={cc} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={intelligenceCountries.includes(cc)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setIntelligenceCountries([...intelligenceCountries, cc]);
                              } else {
                                setIntelligenceCountries(intelligenceCountries.filter(c => c !== cc));
                              }
                            }}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">{geo?.name || cc} ({cc})</span>
                        </label>
                      );
                    })}
                    {targetGeographies.length === 0 && (
                      <p className="col-span-full text-xs text-gray-400">Add target geographies above first</p>
                    )}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Only checked countries get intelligence monitoring (grants, procurement, permits).
                  Your main RSS/Google News search is not affected.
                </p>
              </div>

              {/* PKD/NACE Codes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Industry Codes (PKD/NACE)
                  <span className="text-xs text-gray-500 ml-2">Optional, for procurement filtering</span>
                  <button type="button" onClick={() => setShowPkdRef(!showPkdRef)} className="ml-1.5 text-gray-400 hover:text-primary-600 align-middle">
                    <HelpCircle className="w-3.5 h-3.5 inline" />
                  </button>
                </label>
                {showPkdRef && (
                  <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-gray-700">
                    <p className="font-semibold text-green-800 mb-1.5">Common Food Industry PKD/NACE Codes:</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mb-2">
                      <span><strong>10.71</strong> — Bread & bakery</span>
                      <span><strong>10.72</strong> — Biscuits & pastry</span>
                      <span><strong>10.73</strong> — Pasta & noodles</span>
                      <span><strong>10.61</strong> — Grain milling</span>
                      <span><strong>10.62</strong> — Starches & starch products</span>
                      <span><strong>10.51</strong> — Dairy processing</span>
                      <span><strong>10.52</strong> — Ice cream</span>
                      <span><strong>10.82</strong> — Cocoa & confectionery</span>
                      <span><strong>10.83</strong> — Tea & coffee processing</span>
                      <span><strong>10.85</strong> — Ready meals</span>
                      <span><strong>10.89</strong> — Other food products</span>
                      <span><strong>10.91</strong> — Animal feed</span>
                      <span><strong>10.92</strong> — Pet food</span>
                      <span><strong>10.11</strong> — Meat processing</span>
                      <span><strong>10.20</strong> — Fish processing</span>
                      <span><strong>10.31</strong> — Potato processing</span>
                      <span><strong>10.39</strong> — Fruit & veg processing</span>
                      <span><strong>10.41</strong> — Oils & fats</span>
                      <span><strong>10.84</strong> — Condiments & seasonings</span>
                      <span><strong>11.05</strong> — Beer brewing</span>
                    </div>
                    <p className="text-green-700 text-xs mt-1 font-medium">Tip: Add multiple codes to broaden your signal coverage. For bakery equipment sellers, try 10.71 + 10.72 + 10.73 + 10.82 + 10.61.</p>
                    <p className="text-gray-500">Full list: <a href="https://ec.europa.eu/eurostat/ramon/nomenclatures/index.cfm?TargetUrl=LST_NOM_DTL&StrNom=NACE_REV2" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Eurostat NACE Rev.2</a> · Same codes work for Polish PKD, Finnish TOL, and all EU countries.</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {pkdCodes.map((code, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs px-2 py-1 rounded-lg border border-green-200">
                      {code}
                      <button type="button" onClick={() => setPkdCodes(pkdCodes.filter((_, j) => j !== i))} className="text-green-400 hover:text-green-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPkdCode}
                    onChange={(e) => setNewPkdCode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = newPkdCode.trim();
                        if (v && !pkdCodes.includes(v)) { setPkdCodes([...pkdCodes, v]); setNewPkdCode(''); }
                      }
                    }}
                    placeholder="e.g. 10.71"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const v = newPkdCode.trim();
                      if (v && !pkdCodes.includes(v)) { setPkdCodes([...pkdCodes, v]); setNewPkdCode(''); }
                    }}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* CPV Codes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Procurement Codes (CPV)
                  <span className="text-xs text-gray-500 ml-2">Optional, for EU tender filtering</span>
                  <button type="button" onClick={() => setShowCpvRef(!showCpvRef)} className="ml-1.5 text-gray-400 hover:text-primary-600 align-middle">
                    <HelpCircle className="w-3.5 h-3.5 inline" />
                  </button>
                </label>
                {showCpvRef && (
                  <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-gray-700">
                    <p className="font-semibold text-blue-800 mb-1.5">Common Equipment & Works CPV Codes:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 mb-2">
                      <span><strong>42200000</strong> — Food processing machinery</span>
                      <span><strong>42900000</strong> — Misc. general-purpose machinery</span>
                      <span><strong>42300000</strong> — Industrial furnaces & ovens</span>
                      <span><strong>42500000</strong> — Cooling & ventilation</span>
                      <span><strong>45100000</strong> — Site preparation work</span>
                      <span><strong>45210000</strong> — Building construction</span>
                      <span><strong>45213200</strong> — Warehouse construction</span>
                      <span><strong>45213240</strong> — Agricultural building works</span>
                    </div>
                    <p className="text-gray-500">Use prefix wildcards like <strong>42*</strong> (all machinery) or <strong>451*</strong> (site preparation). Full list: <a href="https://simap.ted.europa.eu/cpv" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">EU SIMAP CPV Browser</a></p>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {cpvCodes.map((code, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-lg border border-blue-200">
                      {code}
                      <button type="button" onClick={() => setCpvCodes(cpvCodes.filter((_, j) => j !== i))} className="text-blue-400 hover:text-blue-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCpvCode}
                    onChange={(e) => setNewCpvCode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = newCpvCode.trim();
                        if (v && !cpvCodes.includes(v)) { setCpvCodes([...cpvCodes, v]); setNewCpvCode(''); }
                      }
                    }}
                    placeholder="e.g. 42200000"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const v = newCpvCode.trim();
                      if (v && !cpvCodes.includes(v)) { setCpvCodes([...cpvCodes, v]); setNewCpvCode(''); }
                    }}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Keyword Axes — Two-axis keyword matching */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-medium text-gray-900">Signal Keyword Axes</span>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Advanced</span>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  Two-axis matching: signals pass pre-filter only when BOTH Axis A (sector) AND Axis B (event) match.
                  Axis C keywords (your products) always pass alone. This eliminates noise from generic mentions.
                </p>

                {/* Axis A — Sector */}
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <label className="block text-sm font-semibold text-green-800 mb-1">Axis A — Sector (what they do)</label>
                  <p className="text-xs text-green-600 mb-2">Keywords describing your target customers' business type</p>
                  {(intelligenceMode === 'specific' ? intelligenceCountries : targetGeographies).map(cc => {
                    const geo = GEO_OPTIONS.find(g => g.code === cc);
                    const langs = GEO_TO_LANGUAGES[cc] || ['en'];
                    return langs.map(lang => {
                      const keywords_list = axisASector[lang] || [];
                      const inputKey = `a_${lang}`;
                      return (
                        <div key={`axis_a_${cc}_${lang}`} className="mb-2">
                          <span className="text-xs font-medium text-gray-600">{lang.toUpperCase()} ({geo?.name || cc})</span>
                          <div className="flex flex-wrap gap-1 mt-1 mb-1">
                            {keywords_list.map((kw, i) => (
                              <span key={i} className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-lg">
                                {kw}
                                <button type="button" onClick={() => removeAxisKeyword('a', lang, kw)} className="text-green-400 hover:text-green-600"><X className="w-3 h-3" /></button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-1">
                            <input type="text" value={newAxisKeyword[inputKey] || ''} onChange={(e) => setNewAxisKeyword({...newAxisKeyword, [inputKey]: e.target.value})} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAxisKeyword('a', setAxisASector, lang); }}} placeholder={lang === 'pl' ? 'e.g. piekarnia, cukiernia' : 'e.g. bakery, confectionery'} className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-green-500" />
                            <button type="button" onClick={() => addAxisKeyword('a', setAxisASector, lang)} className="px-2 py-1 bg-green-100 hover:bg-green-200 rounded text-xs"><Plus className="w-3 h-3" /></button>
                          </div>
                        </div>
                      );
                    });
                  })}
                </div>

                {/* Axis B — Event */}
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <label className="block text-sm font-semibold text-blue-800 mb-1">Axis B — Event (what happened)</label>
                  <p className="text-xs text-blue-600 mb-2">Keywords describing buying events and investment triggers</p>
                  {(intelligenceMode === 'specific' ? intelligenceCountries : targetGeographies).map(cc => {
                    const geo = GEO_OPTIONS.find(g => g.code === cc);
                    const langs = GEO_TO_LANGUAGES[cc] || ['en'];
                    return langs.map(lang => {
                      const keywords_list = axisBEvent[lang] || [];
                      const inputKey = `b_${lang}`;
                      return (
                        <div key={`axis_b_${cc}_${lang}`} className="mb-2">
                          <span className="text-xs font-medium text-gray-600">{lang.toUpperCase()} ({geo?.name || cc})</span>
                          <div className="flex flex-wrap gap-1 mt-1 mb-1">
                            {keywords_list.map((kw, i) => (
                              <span key={i} className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-lg">
                                {kw}
                                <button type="button" onClick={() => removeAxisKeyword('b', lang, kw)} className="text-blue-400 hover:text-blue-600"><X className="w-3 h-3" /></button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-1">
                            <input type="text" value={newAxisKeyword[inputKey] || ''} onChange={(e) => setNewAxisKeyword({...newAxisKeyword, [inputKey]: e.target.value})} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAxisKeyword('b', setAxisBEvent, lang); }}} placeholder={lang === 'pl' ? 'e.g. inwestycja, rozbudowa, nowa linia' : 'e.g. investment, expansion, new line'} className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500" />
                            <button type="button" onClick={() => addAxisKeyword('b', setAxisBEvent, lang)} className="px-2 py-1 bg-blue-100 hover:bg-blue-200 rounded text-xs"><Plus className="w-3 h-3" /></button>
                          </div>
                        </div>
                      );
                    });
                  })}
                </div>

                {/* Axis C — Product */}
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <label className="block text-sm font-semibold text-purple-800 mb-1">Axis C — Your Products (always relevant)</label>
                  <p className="text-xs text-purple-600 mb-2">Keywords for your specific products — any mention passes filter automatically</p>
                  {(intelligenceMode === 'specific' ? intelligenceCountries : targetGeographies).map(cc => {
                    const geo = GEO_OPTIONS.find(g => g.code === cc);
                    const langs = GEO_TO_LANGUAGES[cc] || ['en'];
                    return langs.map(lang => {
                      const keywords_list = axisCProduct[lang] || [];
                      const inputKey = `c_${lang}`;
                      return (
                        <div key={`axis_c_${cc}_${lang}`} className="mb-2">
                          <span className="text-xs font-medium text-gray-600">{lang.toUpperCase()} ({geo?.name || cc})</span>
                          <div className="flex flex-wrap gap-1 mt-1 mb-1">
                            {keywords_list.map((kw, i) => (
                              <span key={i} className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-lg">
                                {kw}
                                <button type="button" onClick={() => removeAxisKeyword('c', lang, kw)} className="text-purple-400 hover:text-purple-600"><X className="w-3 h-3" /></button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-1">
                            <input type="text" value={newAxisKeyword[inputKey] || ''} onChange={(e) => setNewAxisKeyword({...newAxisKeyword, [inputKey]: e.target.value})} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAxisKeyword('c', setAxisCProduct, lang); }}} placeholder={lang === 'pl' ? 'e.g. dozownik, podajnik mąki' : 'e.g. dosing system, flour handling'} className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-purple-500" />
                            <button type="button" onClick={() => addAxisKeyword('c', setAxisCProduct, lang)} className="px-2 py-1 bg-purple-100 hover:bg-purple-200 rounded text-xs"><Plus className="w-3 h-3" /></button>
                          </div>
                        </div>
                      );
                    });
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ─── Submit ─── */}
        {submitError && (
          <div className="flex items-start p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{submitError}</span>
          </div>
        )}

        <div className="flex flex-col items-center space-y-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto px-8 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {isEdit ? 'Saving...' : 'Creating Profile...'}
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                {isEdit ? 'Save Changes' : 'Create Profile & Start Monitoring'}
              </>
            )}
          </motion.button>

          <div className="flex items-start text-xs text-gray-500 max-w-md text-center">
            <Info className="w-4 h-4 mr-1.5 flex-shrink-0 mt-0.5" />
            <span>
              Monitoring starts immediately. Most clients see their first signals
              within the first couple of weeks, depending on market activity.
              First 20 signals are reviewed by admin before delivery.
            </span>
          </div>
        </div>
      </form>
    </motion.div>
  );
};

export default SignalProfileSetup;
